import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { UIDAPresentationTypeEnum } from '@kaltura-ott/tvpil';
import { useContinueWatching, useContinueWatchingGridSettings, WatchedAsset } from '@kaltura-ott/tvpil-shared';

import { DEFAULT_IMAGE_PARAMS } from 'consts';
import { WidgetProviderData } from 'context/widgetProvider/types';
import { useLoadingPerformanceEvents } from 'hooks';
import { useRhinoErrorReaction } from 'hooks/common/useRhinoErrorReaction';
import routes from 'routesPaths';
import { APPLICATION_SPINNER_EVENT_NAME, applyStyleHexToHSL, applyStyles } from 'utils';

import { COMPONENT_NAME, PAGE_SIZE } from '../../constants';
import { useGridMatchPath } from '../../hooks/useGridMatchPath/useGridMatchPath';

function useContinueWatchingGrid() {
  const { type, id } = useGridMatchPath(routes.continueWatchingGrid.path);
  const [, setForceUpdate] = useState(false);
  const gridType = UIDAPresentationTypeEnum.cw;
  const settings = useContinueWatchingGridSettings({
    gridReferenceId: type,
    railReferenceId: id,
    ...DEFAULT_IMAGE_PARAMS
  });

  const { items, totalCount, isLoading, hasNextPage, fetchNextPage, error, refetch } = useContinueWatching({
    referenceId: id,
    pageSize: PAGE_SIZE,
  });

  const data: WidgetProviderData<WatchedAsset> = {
    items,
    totalCount,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch,
  };

  const wrapperRef = useRef(null);
  const intl = useIntl();
  const shouldSendAppLoadingPerformanceEvent = isLoading && !data.items.length;
  const notValidPageTitle = intl.formatMessage({
    id: `${COMPONENT_NAME}.notValidPage`,
    defaultMessage: 'Not a valid page',
  });

  useEffect(() => {
    function initGridPage() {
      if (!settings) {
        return;
      }

      const node = wrapperRef?.current;

      if (node && settings.branding) {
        applyStyles(node, settings.branding, false, true);
        applyStyleHexToHSL(node, settings.branding.backgroundColor);
      }
    }

    initGridPage();
  }, [type, id, settings]);

  useRhinoErrorReaction({ error, refetch });

  useLoadingPerformanceEvents(APPLICATION_SPINNER_EVENT_NAME.GRID_PAGE, shouldSendAppLoadingPerformanceEvent);

  return {
    data,
    settings,
    gridType,
    wrapperRef,
    setForceUpdate,
    notValidPageTitle,
    shouldSendAppLoadingPerformanceEvent,
  };
}
export { useContinueWatchingGrid };
